import {
  computed,
  inject,
  Injectable,
  Injector,
  resource,
  signal,
  Signal,
  TemplateRef,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  MbscCellClickEvent,
  MbscEventClickEvent,
  MbscEventCreateEvent,
  MbscEventDeleteEvent,
  MbscEventDragEvent,
  MbscResource,
} from '@mobiscroll/angular';
import {
  CalendarViewType,
  Department,
  DepartmentHead,
  DetailEmployee,
  Employee,
  LoadRosterDetailParams,
  PmsCalendarResource,
  RosterContent,
  RosterEventRepository,
  RosterResourceRepository,
  RosterService,
  RosterStructureRepository,
  setTimeOfDate,
  SOFTLINE_ROSTER_TIME_CONFIG,
  Structure,
} from '@softapps/pms/roster';
import {
  GarageOrder,
  NotificationService,
  OrderService,
  OrderTypeEnum,
} from '@softapps/werkstatt/garage-core';
import { block, handleRequestError, MenuItem } from '@softline/application';
import { DateTimeService, toIsoDate } from '@softline/core';
import { askDeleteFn, MessageBarService, modalFn } from '@softline/ui-core';
import { GarageRosterTaskFormComponent } from '../modals/garage-roster-task-form/garage-roster-task-form.modal';
import { GarageSmsTemplateComponent } from '../modals/garage-sms-template/garage-sms-template.component';

// Loaded on demand: the order form pulls @softapps/wws/core + @softline/dynamic,
// which must stay out of this remote's boot-time globals closure.
const loadOrderFormComponent = () =>
  import('../modals/order-form/order-form.component').then((m) => m.OrderFormComponent);

@Injectable()
export class GarageRosterService extends RosterService<RosterContent> {
  override readonly calendarDetails = signal<Signal<'compact' | 'all'>>(signal('all'));
  readonly #injector = inject(Injector);
  readonly #router = inject(Router);

  override refreshEvents(): void {
    this.#events.reload();
  }

  private readonly timeConfig = inject(SOFTLINE_ROSTER_TIME_CONFIG, {
    optional: true,
  }) ?? {
    startHour: 6,
    endHour: 20,
  };

  readonly contextMenuEventDate = signal<Date | null>(null);
  readonly contextMenuResource = signal<number | string | null>(null);
  override readonly copyEvents = signal<RosterContent[]>([]);

  readonly messageBarService = inject(MessageBarService);

  readonly #garageOrderService = inject(OrderService);
  readonly #notificationService = inject(NotificationService);

  // repositories
  readonly #structureRepository = inject<RosterStructureRepository<Structure>>(
    RosterStructureRepository
  );
  readonly #eventRepository: RosterEventRepository<RosterContent> = inject(
    RosterEventRepository<RosterContent>
  );
  readonly #resourceRepository: RosterResourceRepository<PmsCalendarResource, any> =
    inject(RosterResourceRepository<PmsCalendarResource, any>);

  // modal
  readonly #modal = modalFn();
  readonly #askDelete = askDeleteFn();

  // PARAMETERS FOR GARAGE ROSTER SERVICES
  readonly params = computed(() => {
    const beginDate = this.startOfPeriod();
    const endDate = this.endOfPeriod();
    const departmentHead = this.selectedDepartmentHead()();
    if (!departmentHead || !beginDate) return undefined;
    return {
      pathParams: {
        employeeId: departmentHead.employee.id,
      },
      queryParams: {
        begindate: toIsoDate(beginDate),
        enddate: toIsoDate(endDate),
      },
    };
  });

  override readonly menuItems = computed<MenuItem[]>(() => [
    {
      name: 'garage-roster',
      title: 'Dienstplaner',
      icon: 'fa-regular fa-calendar-days',
      type: 'route',
      outlet: 'nav',
      routerLink: ['/roster/garage'],
      group: 'Werkstatt',
    },
  ]);

  readonly openNotificationModal = async () => {
    const events = this.selectedEvents();

    for (const event of events) {
      const orderId = event.additionalData.additionaldetail[0].tabid;
      const type =
        event?.additionalData.additionaldetail[0].name === 'Handelsauftrag'
          ? OrderTypeEnum.PRODUCT
          : OrderTypeEnum.GARAGE;
      let order: GarageOrder | null = null;
      try {
        order =
          type === OrderTypeEnum.GARAGE
            ? await this.#garageOrderService.getGarageOrderById(orderId)
            : await this.#garageOrderService.getProductOrderById(orderId);
      } catch (e) {
        handleRequestError(e, this.#injector, { show: true });
        continue;
      }

      const object = order?.object;
      const objectLabel = object
        ? `${object.number ?? ''} ${object.name ?? ''}`.trim()
        : '';
      const title = objectLabel
        ? `SMS an Kunden senden - OBJEKT: ${objectLabel}`
        : 'SMS an Kunden senden';

      const result = await this.#modal({
        component: GarageSmsTemplateComponent,
        dismiss: false,
        title,
        data: {
          id: orderId,
          phone: order?.phone,
        },
      });

      if (result === 'DISMISSED' || result === 'CANCEL') {
        continue;
      }

      if (result) {
        this.#notificationService.sendNotification(orderId, {
          phone: result.phone,
          content: result.content,
        });
      }
    }
  };

  readonly #beginDateSource = signal<Signal<string | null>>(
    signal(inject(DateTimeService).now())
  );

  readonly #calendarViewTypeSource = signal<Signal<CalendarViewType>>(
    signal<CalendarViewType>('week')
  );

  readonly #columnWidthSource = signal<Signal<string>>(signal('xxlarge'));

  override readonly startOfPeriod = computed<Date>(() => {
    const dateStr = this.#beginDateSource()();
    const calendarViewType = this.#calendarViewTypeSource()();

    if (!dateStr || !calendarViewType) return new Date();
    const date = new Date(dateStr);

    switch (calendarViewType) {
      case 'day':
        return date;

      case 'week': {
        const currentDay = date.getDay();
        const daysToStartOfWeek = currentDay === 0 ? 6 : currentDay - 1;
        const startOfWeek = new Date(date);
        startOfWeek.setDate(date.getDate() - daysToStartOfWeek);
        return startOfWeek;
      }

      case 'month': {
        const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        return startOfMonth;
      }

      default:
        return date;
    }
  });

  override readonly endOfPeriod = computed<Date>(() => {
    const dateStr = this.#beginDateSource()();
    const calendarViewType = this.#calendarViewTypeSource()();

    if (!dateStr || !calendarViewType) return new Date();
    const date = new Date(dateStr);

    switch (calendarViewType) {
      case 'day':
        return new Date(date.setHours(23, 59, 59));

      case 'week': {
        const currentDay = date.getDay();
        const daysToEndOfWeek = currentDay === 0 ? 0 : 7 - currentDay;
        const endOfWeek = new Date(date);
        endOfWeek.setDate(date.getDate() + daysToEndOfWeek);
        return endOfWeek;
      }

      case 'month': {
        const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        return endOfMonth;
      }

      default:
        return date;
    }
  });

  // GARAGE ROSTER STRUCTURE RETRIEVAL
  readonly #structure = resource({
    params: () => this.params(),
    loader: async ({ params }) => {
      if (!params) return Promise.resolve(undefined);
      return this.#structureRepository.load({
        employeeId: params.pathParams.employeeId, // department head
        begindate: params.queryParams.begindate,
        enddate: params.queryParams.enddate,
      });
    },
  });
  readonly structure = computed(() => this.#structure.value());

  readonly garageEventMenu = signal<TemplateRef<unknown> | undefined>(undefined);

  // GARAGE ROSTER EVENTS RETRIEVAL
  readonly #events = resource({
    params: () => {
      const resources = this.resources();
      if (!resources || resources.length < 1) return undefined;
      return this.params();
    },
    loader: async ({ params }) => {
      if (!params) return Promise.resolve(undefined);
      const departmentHead = this.selectedDepartmentHead()();
      if (!departmentHead) return Promise.resolve(undefined);
      const allEventsMap = new Map<string | number, RosterContent>();
      const children = this.resources().flatMap((o) => o.children ?? []);

      await Promise.all(
        children.map(async (child) => {
          if (!child.id) return;
          try {
            const events = await this.#eventRepository.loadEvents(
              departmentHead.employee.id,
              params.queryParams.begindate,
              params.queryParams.enddate,
              Number(child?.id)
            );
            if (!events || events.length < 1) return;
            events.forEach((event) => {
              if (!event.id) return;
              allEventsMap.set(event?.id, { ...event, resize: true });
            });
          } catch (e) {
            handleRequestError(e, this.#injector, { show: true });
          }
        })
      );

      return allEventsMap;
    },
  });
  readonly events = computed(() =>
    Array.from(this.#events.value()?.values() ?? []).filter((o) => o !== undefined)
  );

  #updateEventsMap(
    updater: (map: Map<string | number, RosterContent>) => void
  ): void {
    const eventMap = this.#events.value();
    if (!eventMap) return;
    updater(eventMap);
    this.#events.value.set(new Map(eventMap));
  }
  readonly resourceFilterString = signal<Signal<string>>(signal(''));

  // GARAGE ROSTER RESOURCE RETRIEVAL
  readonly resources: Signal<MbscResource[]> = computed(() => {
    const structure = this.#structure.value();
    if (!structure) return [];
    const filter = this.resourceFilterString()().trim().toLowerCase();
    return structure.departements
      .map((departement) => {
        return {
          id: departement.department.id,
          name: departement.department.name,
          key: departement.department.key,
          eventCreation: false,
          children: departement.employees
            .map((employee) => {
              return {
                id: employee.id,
                name: employee.name,
                key: employee.key,
                eventCreation: true,
              };
            })
            .filter((e) => !filter || e.name.toLowerCase().includes(filter)),
        };
      })
      .filter((d) => !filter || d.children.length > 0);
  });
  readonly employeeDetail = signal<Map<number | string, DetailEmployee> | null>(null);
  private readonly map = new Map<number | string, DetailEmployee>();

  private async loadDetailGarage(params: LoadRosterDetailParams) {
    const detail = await this.#resourceRepository.loadDetail({
      date: params.date,
      employeeId: params.employeeId,
    });
    this.map.set(params.employeeId, detail);
    this.employeeDetail.set(new Map(this.map));
  }

  readonly selectedEventIds = computed(() => Array.from(this.#selectedEventIds()));
  readonly #selectedEventIds = signal<Set<string | number>>(new Set());
  override readonly selectedEvents = signal<RosterContent[]>([]);
  readonly selectedDepartmentHead = signal<Signal<DepartmentHead | null>>(signal(null));

  override registerDepartmentHead(value: Signal<DepartmentHead | null>) {
    this.selectedDepartmentHead.set(value);
  }

  override registerBeginDate(value: Signal<string>) {
    this.#beginDateSource.set(value);
  }

  override registerCalenderViewType(value: Signal<CalendarViewType>): void {
    this.#calendarViewTypeSource.set(value);
  }

  override registerPlanMonthRange(): void {
    /* TODO not used */
  }

  override registerCalendarColumnWidth(value: Signal<string>): void {
    this.#columnWidthSource.set(value);
  }

  override registerCalendarDetails(value: Signal<'compact' | 'all'>): void {
    this.calendarDetails.set(value);
  }

  override registerResourceFilterString(value: Signal<string>): void {
    this.resourceFilterString.set(value);
  }

  override registerEventTypesFilter(value: Signal<string[]>): void {
    console.log(value);
  }

  override async onEventCreate(e: MbscEventCreateEvent, inst: any): Promise<void> {
    if (e.action === 'externalDrop') {
      const start = e.event.start as Date;
      const end = e.event.end as Date;

      // Convert start and end times to UTC
      const startUTC = new Date(
        Date.UTC(
          start.getFullYear(),
          start.getMonth(),
          start.getDate(),
          start.getHours(),
          start.getMinutes(),
          start.getSeconds()
        )
      );

      const endUTC = new Date(
        Date.UTC(
          end.getFullYear(),
          end.getMonth(),
          end.getDate(),
          end.getHours(),
          end.getMinutes() - 10,
          end.getSeconds()
        )
      );
      // check if start and end are 00:00 and 23:50 then set them 08:00 and 18:00
      if (
        startUTC.getHours() === 2 &&
        startUTC.getMinutes() === 0 &&
        endUTC.getHours() === 1 &&
        endUTC.getMinutes() === 50
      ) {
        startUTC.setHours(this.timeConfig.startHour + 2);
        startUTC.setMinutes(0);
        endUTC.setHours(this.timeConfig.endHour + 2);
        endUTC.setMinutes(0);
        endUTC.setDate(startUTC.getDate());
      }

      const startString = startUTC.toISOString().replace('Z', '');
      const endString = endUTC.toISOString().replace('Z', '');

      const structure = this.#structure.value();
      let department: Department | null = null;

      structure?.departements.forEach((d) => {
        if (d.employees.findIndex((emp) => emp.id === e.event.resource) >= 0) {
          department = d.department;
        }
      });

      let additinaldetail: Array<Record<string, unknown>> = [];
      let task = null;
      if (e.event['type'] === 'GARAGE') {
        additinaldetail = [
          {
            tabname: 'ORDER',
            tabid: e.event['id'], // object id
            name: null,
            title: null,
            comment: null,
            state: 'UNDEFINED',
          },
        ];
      } else if (e.event['type'] === 'PRODUCT') {
        additinaldetail = [
          {
            tabname: 'ORDER',
            tabid: e.event['id'], // object id
            name: null,
            title: null,
            comment: null,
            state: 'UNDEFINED',
          },
        ];
        task = null;
      } else if (e.event['type'] === 'UNPRODUCTIVE') {
        additinaldetail = [
          {
            tabname: 'GARAGE_TASK',
            tabid: e.event['id'], // object id
            name: null,
            title: null,
            comment: null,
            state: 'UNDEFINED',
          },
        ];
      }

      const eventTitle =
        e.event['description'] ||
        e.event['label'] ||
        e.event['type'] ||
        'Abwesenheit';

      const body = [
        {
          resource: [e.event.resource],
          date: start,
          allDay: e.event.allDay,
          start: startString,
          end: endString,
          title: eventTitle,
          text: eventTitle,
          additionalData: {
            state: 'UNDEFINED',
            pauseHours: '00:30:00',
            plannedHours: '23:59:59',
            targetHours: '23:59:59',
            employees: [
              {
                employee: {
                  id: e.event.resource,
                  name: e.event['label'],
                } as Employee,
                department: department as unknown as Department,
              },
            ],
            begintime: startString,
            endtime: endString,
            type: 'PLAN',
            task: task,
            department: department,
            ...(additinaldetail.length > 0 ? { additionaldetail: additinaldetail } : {}),
          },
        },
      ] as unknown as RosterContent;
      try {
        const result = await this.#eventRepository.createEvent(body);
        const resultId = result.id;
        if (!resultId) return;
        this.#updateEventsMap((map) =>
          map.set(resultId, { ...result, resize: true })
        );
        const orderId = Number(e.event['id']);
        this.#garageOrderService.setOrderScheduled(orderId);
        if (result['additionalData'].additionaldetail.length > 0) {
          this.messageBarService.success(
            `${result['additionalData'].additionaldetail[0].title} wurde geplant`
          );
        } else {
          this.messageBarService.success(`Tätigkeit/Abwesenheit wurde geplant`); //TODO:
        }
      } catch (e) {
        handleRequestError(e, this.#injector, { show: true });
        this.refreshEvents();
      }
    }
  }

  // EVENT CLICK HANDLERS
  override async onEventClick(args: MbscEventClickEvent, inst: any): Promise<void> {
    this.toggleSelectedEvents(args.event as RosterContent);
  }

  override async onEventDoubleClick(args: MbscEventClickEvent, inst: any): Promise<void> {
    const event = args.event;
    if (!event.resource) return;
    const resource = Array.isArray(event.resource) ? event.resource : [event.resource];
    if (event['additionalData']?.additionaldetail[0]?.tabname === 'ORDER') {
      await this.editOrderWithEvent(resource, event as RosterContent);
    } else {
      await this.editTaskWithEvent(resource, event as RosterContent);
    }
  }

  override async onEventRightClick(args: MbscEventClickEvent, inst: any): Promise<void> {
    const event = args.event as RosterContent;
    if (event.id === undefined) return;

    const selection = this.#selectedEventIds();
    if (!selection.has(event.id)) {
      const updatedSet = new Set(selection);
      updatedSet.add(event.id);
      this.#selectedEventIds.set(updatedSet);
      const events = this.selectedEvents();
      this.selectedEvents.set(
        events.some((e) => e.id === event.id) ? events : [...events, event]
      );
    }

    const mouseEvent = args.domEvent as MouseEvent;
    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();
  }

  /** Prefer the garage-specific event menu; the shared calendar opens it. */
  override resolveEventMenu(): TemplateRef<unknown> | undefined {
    return this.garageEventMenu() ?? this.eventMenu();
  }

  // wird auch bei external Drag aufgerufen
  override async onEventDragStart(args: MbscEventDragEvent, inst: any) {
    //
  }

  // wird auch bei external Drop in Calendar aufgerufen
  override async onEventDragEnd(args: MbscEventDragEvent, inst: any) {
    // Prüfung das es kein External Drop ist (External Events haben kein additionalData)

    if (args.event['additionalData']) {
      const start = args.event.start as Date;
      const end = args.event.end as Date;

      // Convert start and end times to UTC
      const startUTC = new Date(
        Date.UTC(
          start.getFullYear(),
          start.getMonth(),
          start.getDate(),
          start.getHours(),
          start.getMinutes(),
          start.getSeconds()
        )
      );

      const endUTC = new Date(
        Date.UTC(
          end.getFullYear(),
          end.getMonth(),
          end.getDate(),
          end.getHours(),
          end.getMinutes(),
          end.getSeconds()
        )
      );

      const startString = startUTC.toISOString().replace('Z', '');
      const endString = endUTC.toISOString().replace('Z', '');
      await block(async () => {
        try {
          // Backend-only response fields must not be sent back
          const additionalData = { ...args.event['additionalData'] };
          delete additionalData.billable;
          delete additionalData.creatorUser;
          delete additionalData.createdAt;
          const response = await this.#eventRepository.updateEvent({
            ...args.event,
            start: startString,
            end: endString,
            additionalData: {
              ...additionalData,
              type: 'PLAN',
              endtime: endString,
              begintime: startString,
              employees: [
                {
                  employee: {
                    id: args.event.resource?.[0],
                  },
                  department: args.event['additionalData'].employees[0].department,
                },
              ],
              additionaldetail: additionalData.additionaldetail?.map((detail: any) => {
                const cleaned = { ...detail };
                delete cleaned.hoursPlannedArtefact;
                delete cleaned.hoursActualArtefact;
                return cleaned;
              }),
            },
          });
          const updated = response[0];
          const updatedId = updated?.id;
          if (!updatedId) return;
          this.#updateEventsMap((map) =>
            map.set(updatedId, { ...updated, resize: true })
          );
          if (updated.additionalData.additionaldetail.length > 0) {
            this.messageBarService.success(
              `${updated.additionalData.additionaldetail[0].title} wurde verschoben`
            );
          } else {
            this.messageBarService.success(`Tätigkeit/Abwesenheit wurde verschoben`); //TODO:
          }
        } catch (e) {
          handleRequestError(e, this.#injector, { show: true });
          this.refreshEvents();
        }
      }, this.#injector);
    }
  }

  // CELL CLICK HANDLERS
  override async onCellClick(args: MbscCellClickEvent, inst: any): Promise<void> {
    this.#selectedEventIds.set(new Set());
    this.selectedEvents.set([]);
    return Promise.resolve();
  }

  override async onCellDoubleClick(args: MbscCellClickEvent, inst: any): Promise<void> {
    const date = args.date;
    const resource = args.resource;
    await this.createOrderWithDate([resource], date);
    this.#garageOrderService.garageOrder.set(null);
    this.#garageOrderService.productOrder.set(null);
  }

  override async onCellRightClick(args: MbscCellClickEvent, inst: any): Promise<void> {
    this.#selectedEventIds.set(new Set());
    this.selectedEvents.set([]);

    const mouseEvent = args.domEvent as MouseEvent;
    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();

    // The shared calendar opens the overlay; actions read these signals.
    this.contextMenuEventDate.set(args.date ?? null);
    this.contextMenuResource.set((args.resource as number | string) ?? null);
  }

  override onEventDelete(args: MbscEventDeleteEvent, inst: any): boolean {
    const event = args.event as RosterContent;
    if (!event) return false;
    let result = false;
    queueMicrotask(async () => {
      result = await this.deleteSelectedEvents();
    });
    return result;
  }

  // HELPER METHODEN
  override async deleteSelectedEvents(): Promise<boolean> {
    const selectedEvents = this.#selectedEventIds();
    const eventMap = this.#events.value();
    if (selectedEvents.size < 1 || !eventMap) return Promise.resolve(false);
    return await this.deleteEvents(this.getEventsFromSelection(selectedEvents));
  }

  override handleContextMenuAction(action: string): void {
    const selectedEvents = this.selectedEvents();

    switch (action) {
      case 'duplicate':
        if (!selectedEvents.length) return;
        selectedEvents.forEach((event) => this.duplicateEvent(event));
        this.#selectedEventIds.set(new Set());
        this.selectedEvents.set([]);
        break;

      case 'edit': {
        if (!selectedEvents.length) return;
        const event = selectedEvents[0];
        const res = event.resource
          ? Array.isArray(event.resource)
            ? event.resource
            : [event.resource]
          : [];
        if (event.additionalData?.additionaldetail[0]?.tabname === 'ORDER') {
          this.editOrderWithEvent(res, event);
        } else {
          this.editTaskWithEvent(res, event);
        }
        break;
      }

      case 'copyToNextMonday': {
        if (!selectedEvents.length) return;
        const nextMonday = this.#getNextMonday();
        selectedEvents.forEach((event) => this.duplicateEvent(event, nextMonday));
        this.#selectedEventIds.set(new Set());
        this.selectedEvents.set([]);
        break;
      }

      case 'delete':
        if (!selectedEvents.length) return;
        this.deleteSelectedEvents();
        break;

      case 'sms':
        if (!selectedEvents.length) return;
        this.openNotificationModal();
        break;

      case 'copy':
        if (!selectedEvents.length) return;
        this.copyEvents.set(selectedEvents);
        break;

      case 'paste': {
        const copiedEvents = this.copyEvents();
        const targetDate = this.contextMenuEventDate();
        const targetResource = this.contextMenuResource();

        if (!copiedEvents.length || !targetDate || !targetResource) return;
        for (const event of copiedEvents) {
          this.duplicateEvent(event, targetDate as Date, targetResource);
        }
        this.#selectedEventIds.set(new Set());
        this.selectedEvents.set([]);
        break;
      }

      case 'create': {
        const res = this.contextMenuResource();

        this.createOrderWithDate(
          res ? [res as number | string] : [],
          this.contextMenuEventDate() as Date
        );
        break;
      }

      default:
        console.warn('Unknown context menu action:', action);
    }

    this.closeContextMenu();
  }

  override async deleteEvents(events: RosterContent[]): Promise<boolean> {
    if (events.length < 0 || !events) return Promise.resolve(false);
    const result = await this.#askDelete({
      dismiss: true,
      question: 'Möchten Sie diese Aufgabe löschen?',
    });
    if (!result || result === 'DISMISSED' || result === 'CANCEL' || result === 'NO')
      return false;
    try {
      await Promise.all(
        events
          .filter((event) => !!event.id)
          .map((event) => this.#eventRepository.delete(Number(event.id)))
      );
      for (const event of events) {
        if (
          event.additionalData?.additionaldetail[0]?.tabname === 'ORDER' ||
          event.additionalData?.additionaldetail[0]?.tabname === 'OBJECT'
        )
          this.#garageOrderService.removeOrderScheduled(
            event.additionalData.additionaldetail[0].tabid
          );
      }
      this.#updateEventsMap((map) => {
        events.forEach((event) => {
          if (!event.id) return;
          map.delete(event.id);
        });
      });
      return true;
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
      this.refreshEvents();
      return false;
    }
  }

  override async onKeyDown(event: KeyboardEvent): Promise<void> {
    const selectedEvents = this.#selectedEventIds();
    const eventMap = this.#events.value();
    if (selectedEvents.size < 1 || !eventMap) return;
    if (event.key === 'Unidentified' || event.key === '') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    /*if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }*/
  }

  private toggleSelectedEvents(event: RosterContent) {
    if (!event.id) return;
    const selection = this.#selectedEventIds();
    const events = this.selectedEvents();
    if (selection.has(event.id)) {
      selection.delete(event.id);
      this.#selectedEventIds.set(new Set(selection));
      const updatedEvents = events.filter((e) => e.id !== event.id);
      this.selectedEvents.set(updatedEvents);
    } else {
      const updatedSet = selection.add(event.id);
      this.#selectedEventIds.set(new Set(updatedSet));
      this.selectedEvents.set([...events, event]);
    }
  }

  private getEventsFromSelection(eventIds: Set<string | number>): RosterContent[] {
    const eventMap = this.#events.value();
    if (!eventMap) return [];
    return Array.from(eventIds)
      .filter((o) => o !== undefined)
      .map((eventId) => eventMap.get(eventId))
      .filter((o) => o !== undefined);
  }

  async loadEmployeeContent(structure: Structure, date: string): Promise<void> {
    /*let currentChunk: Employee[] = [];
    for (const depStructure of structure.departements) {
      for (const employee of depStructure.employees) {
        currentChunk.push(employee);
        if (currentChunk.length < 2) continue;
        await Promise.all(
          currentChunk.map((e) => this.loadDetailGarage({ date: date, employeeId: e.id }))
        );
        currentChunk = [];
      }
    }
    if (currentChunk.length > 0) {
      await Promise.all(
        currentChunk.map((e) => this.loadDetailGarage({ date: date, employeeId: e.id }))
      );
    }*/
  }

  async createOrderWithDate(resource: (number | string)[], date: Date | string | object) {
    this.#garageOrderService.loadGarageOrderById(null);
    this.#garageOrderService.loadProductOrderById(null);
    this.#garageOrderService.garageOrder.set(null);
    this.#garageOrderService.productOrder.set(null);

    const savedTab = Number(localStorage.getItem('garage-roster-tab') ?? 0);
    const orderType = savedTab === 0 ? OrderTypeEnum.GARAGE : OrderTypeEnum.PRODUCT;

    const data = await this.#modal({
      component: await loadOrderFormComponent(),
      title: 'Auftrag erstellen',
      subtitle: 'Details zum Auftrag festlegen',
      icon: 'fa-regular fa-clipboard-list',
      class: 'wide',
      separateContentSection: true,
      surfaceControls: { minimize: true, dock: true },
      data: {
        orderType,
        date: date,
        resource: resource,
        resources: this.resources(),
      },
    });

    if (!data || data === 'DISMISSED') return;
    const orderResponse: GarageOrder | null = data.order ?? null;
    if (!orderResponse?.id || !data.event) return;

    this.#garageOrderService.setOrderScheduled(orderResponse?.id ?? 0);
    await this.#createTermin(orderResponse, data.event, resource);
  }

  async #createTermin(
    orderResponse: GarageOrder,
    event: {
      beginTime: string;
      endTime: string;
      beginDay: string;
      endDay: string;
      employeeId: number | string;
    },
    resource?: (number | string)[]
  ): Promise<void> {
    const targetResource =
      resource && resource.length > 0 ? resource[0] : Number(event.employeeId);
    if (targetResource == null) return;

    const normalizedBeginTime = event.beginTime.startsWith('T')
      ? event.beginTime
      : `T${event.beginTime}`;
    const normalizedEndTime = event.endTime.startsWith('T')
      ? event.endTime
      : `T${event.endTime}`;

    const start = setTimeOfDate(event.beginDay, normalizedBeginTime) as Date;
    const end = setTimeOfDate(event.endDay, normalizedEndTime) as Date;

    const startUTC = new Date(
      Date.UTC(
        start.getFullYear(),
        start.getMonth(),
        start.getDate(),
        start.getHours(),
        start.getMinutes(),
        start.getSeconds()
      )
    );
    const endUTC = new Date(
      Date.UTC(
        end.getFullYear(),
        end.getMonth(),
        end.getDate(),
        end.getHours(),
        end.getMinutes(),
        end.getSeconds()
      )
    );
    // check if start and end are 00:00 and 23:50 then set them 08:00 and 18:00
    if (
      startUTC.getHours() === 2 &&
      startUTC.getMinutes() === 0 &&
      endUTC.getHours() === 1 &&
      endUTC.getMinutes() === 0
    ) {
      startUTC.setHours(10);
      startUTC.setMinutes(0);
      endUTC.setHours(19);
      endUTC.setMinutes(0);
      endUTC.setDate(startUTC.getDate());
    }

    const startString = startUTC.toISOString().replace('Z', '');
    const endString = endUTC.toISOString().replace('Z', '');
    const additinaldetail = [
      {
        tabname: 'ORDER',
        tabid: orderResponse?.id,
        name: null,
        title: null,
        comment: null,
        state: 'UNDEFINED',
      },
    ];
    const department = this.#departmentForResource(targetResource);
    const body = [
      {
        resource: [targetResource],
        date: startString,
        allDay: false,
        start: startString,
        end: endString,
        title: '',
        text: '',
        additionalData: {
          state: 'UNDEFINED',
          pauseHours: '00:30:00',
          plannedHours: '23:59:59',
          targetHours: '23:59:59',
          department,
          employees: [
            {
              employee: { id: Number(event.employeeId) } as Employee,
              department,
            },
          ],
          begintime: startString,
          endtime: endString,
          type: 'PLAN',
          task: null,
          additionaldetail: additinaldetail,
        },
      },
    ] as unknown as RosterContent;
    try {
      const result = await this.#eventRepository.createEvent(body);
      const resultId = result.id;
      if (!resultId) return;
      this.#updateEventsMap((map) =>
        map.set(resultId, { ...result, resize: true })
      );
      if (result['additionalData'].additionaldetail.length > 0) {
        this.messageBarService.success(
          `${result['additionalData'].additionaldetail[0].title ?? 'Termin'} wurde geplant`
        );
      }
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
      this.refreshEvents();
    }
  }

  #departmentForResource(resourceId: number | string): Department | null {
    const id = Number(resourceId);
    const structure = this.#structure.value();
    const match = structure?.departements.find((d) =>
      d.employees.some((e) => e.id === id)
    );
    return match?.department ?? null;
  }

  async createOrder(orderType: OrderTypeEnum) {
    this.#garageOrderService.loadGarageOrderById(null);
    this.#garageOrderService.loadProductOrderById(null);
    this.#garageOrderService.garageOrder.set(null);
    this.#garageOrderService.productOrder.set(null);
    const data = await this.#modal({
      component: await loadOrderFormComponent(),
      title: 'Auftrag erstellen',
      subtitle: 'Details zum Auftrag festlegen',
      icon: 'fa-regular fa-clipboard-list',
      class: 'wide',
      separateContentSection: true,
      surfaceControls: { minimize: true, dock: true },
      data: {
        orderType,
        resources: this.resources(),
      },
    });

    if (!data || data === 'DISMISSED') return;
    // The modal already persisted the order.
    const result: GarageOrder | null = data.order ?? null;
    if (!result?.id) return;

    this.messageBarService.success(
      ` ${result.description} für ${result.customer?.name1 ?? ''} wurde erstellt.`
    );

    if (data.event) {
      this.#garageOrderService.setOrderScheduled(result.id);
      await this.#createTermin(result, data.event);
    }
  }

  openOrderInNewTab(orderId: number, type: OrderTypeEnum): void {
    if (!orderId) return;
    const url = this.#router.serializeUrl(
      this.#router.createUrlTree(['/order', orderId], {
        queryParams:
          type === OrderTypeEnum.PRODUCT ? { type: OrderTypeEnum.PRODUCT } : {},
      })
    );
    window.open(url, '_blank');
  }

  async editOrder(orderId: number, type: OrderTypeEnum, navTabIndex = 0) {
    if (orderId) {
      if (type === OrderTypeEnum.GARAGE) {
        this.#garageOrderService.loadGarageOrderById(Number(orderId));
      } else if (type === OrderTypeEnum.PRODUCT) {
        this.#garageOrderService.loadProductOrderById(Number(orderId));
      }
    }
    const data = await this.#modal({
      component: await loadOrderFormComponent(),
      title: 'Auftrag bearbeiten',
      subtitle: 'Details zum Auftrag anpassen',
      icon: 'fa-regular fa-clipboard-list',
      class: 'wide',
      separateContentSection: true,
      surfaceControls: { minimize: true, dock: true },
      data: {
        orderType: type,
        editMode: true,
        navTabIndex: navTabIndex,
        resources: this.resources(),
      },
    });
    if (!data || data === 'DISMISSED') return;
    const order: GarageOrder | null = data.order ?? null;
    if (order?.id && data.event) {
      this.#garageOrderService.setOrderScheduled(order.id);
      await this.#createTermin(order, data.event);
    }
  }

  async editOrderWithEvent(resource: (number | string)[], event: RosterContent) {
    const orderId = event?.additionalData.additionaldetail[0].tabid;

    const type =
      event?.additionalData.additionaldetail[0].name === 'Handelsauftrag'
        ? OrderTypeEnum.PRODUCT
        : OrderTypeEnum.GARAGE;
    if (orderId) {
      if (type === OrderTypeEnum.GARAGE) {
        this.#garageOrderService.loadGarageOrderById(Number(orderId));
      } else if (type === OrderTypeEnum.PRODUCT) {
        this.#garageOrderService.loadProductOrderById(Number(orderId));
      }
    }
    const data = await this.#modal({
      component: await loadOrderFormComponent(),
      title: 'Auftrag bearbeiten',
      subtitle: 'Details zum Auftrag anpassen',
      icon: 'fa-regular fa-clipboard-list',
      class: 'wide',
      separateContentSection: true,
      surfaceControls: { minimize: true },
      data: {
        orderType: type,
        resource: resource,
        event: event,
        resources: this.resources(),
        navTabIndex: 3,
        editMode: true,
      },
    });
    if (!data || data === 'DISMISSED') return;
    if (!data.event) return;

    const normalizedBeginTime = data.event.beginTime.startsWith('T')
      ? data.event.beginTime
      : `T${data.event.beginTime}`;
    const normalizedEndTime = data.event.endTime.startsWith('T')
      ? data.event.endTime
      : `T${data.event.endTime}`;

    const start = setTimeOfDate(data.event.beginDay, normalizedBeginTime) as Date;
    const end = setTimeOfDate(data.event.endDay, normalizedEndTime) as Date;

    // Convert start and end times to UTC
    const startUTC = new Date(
      Date.UTC(
        start.getFullYear(),
        start.getMonth(),
        start.getDate(),
        start.getHours(),
        start.getMinutes(),
        start.getSeconds()
      )
    );

    const endUTC = new Date(
      Date.UTC(
        end.getFullYear(),
        end.getMonth(),
        end.getDate(),
        end.getHours(),
        end.getMinutes(),
        end.getSeconds()
      )
    );
    // check if start and end are 00:00 and 23:50 then set them 08:00 and 18:00
    if (
      startUTC.getHours() === 2 &&
      startUTC.getMinutes() === 0 &&
      endUTC.getHours() === 1 &&
      endUTC.getMinutes() === 0
    ) {
      startUTC.setHours(10);
      startUTC.setMinutes(0);
      endUTC.setHours(19);
      endUTC.setMinutes(0);
      endUTC.setDate(startUTC.getDate());
    }

    const startString = startUTC.toISOString().replace('Z', '');
    const endString = endUTC.toISOString().replace('Z', '');

    // Backend-only response fields must not be sent back
    const additionalData = { ...event.additionalData };
    delete additionalData.billable;
    delete additionalData.creatorUser;
    delete additionalData.createdAt;
    const detail = { ...event.additionalData.additionaldetail[0] };
    delete detail.hoursPlannedArtefact;
    delete detail.hoursActualArtefact;
    const body = {
      ...event,
      start: startString,
      end: endString,
      additionalData: {
        ...additionalData,
        endtime: endString,
        comment: data.event.comment,
        employees: [
          {
            employee: {
              ...event.additionalData.employees[0].employee,
              id: data.event.employeeId ?? event.additionalData.employees[0].employee.id,
            },
            department: event.additionalData.employees[0].department,
          },
        ],
        additionaldetail: [
          {
            ...detail,
            comment:
              data.event.comment ?? event.additionalData.additionaldetail[0].comment,
          },
        ],
        begintime: startString,
      },
    } as RosterContent;
    try {
      const result = await this.#eventRepository.updateEvent(body);
      const updated = result[0];
      const updatedId = updated?.id;
      if (!updatedId) return;
      this.#updateEventsMap((map) =>
        map.set(updatedId, { ...updated, resize: true })
      );
      this.messageBarService.success(`Auftrag wurde erfolgreich bearbeitet.`);
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
      this.refreshEvents();
    }
  }

  async editTaskWithEvent(resource: (number | string)[], event: RosterContent) {
    const data = await this.createOrEditTask(resource, event);
    if (!data) return;

    let changedBeginTime: Date | null = null;
    let changedEndTime: Date | null = null;

    // Normalize time strings to ensure they include 'T'
    const normalizedBeginTime = data.beginTime.startsWith('T')
      ? data.beginTime
      : `T${data.beginTime}`;
    const normalizedEndTime = data.endTime.startsWith('T')
      ? data.endTime
      : `T${data.endTime}`;

    changedBeginTime = setTimeOfDate(data.beginDay, normalizedBeginTime);
    changedEndTime = setTimeOfDate(data.endDay, normalizedEndTime);

    let startString: string;
    let endString: string;
    if (!changedBeginTime) startString = event.start as string;
    else {
      changedBeginTime.setHours(changedBeginTime.getHours());
      changedBeginTime = new Date(
        Date.UTC(
          changedBeginTime.getFullYear(),
          changedBeginTime.getMonth(),
          changedBeginTime.getDate(),
          changedBeginTime.getHours(),
          changedBeginTime.getMinutes(),
          changedBeginTime.getSeconds()
        )
      );
      startString = changedBeginTime.toISOString();
      startString = startString.replace('Z', '');
    }

    if (!changedEndTime) endString = event.end as string;
    else {
      changedEndTime = new Date(
        Date.UTC(
          changedEndTime.getFullYear(),
          changedEndTime.getMonth(),
          changedEndTime.getDate(),
          changedEndTime.getHours(),
          changedEndTime.getMinutes(),
          changedEndTime.getSeconds()
        )
      );
      endString = changedEndTime.toISOString();
      endString = endString.replace('Z', '');
    }
    // Backend-only response fields must not be sent back
    const additionalData = { ...event.additionalData };
    delete additionalData.billable;
    delete additionalData.creatorUser;
    delete additionalData.createdAt;
    const detail = { ...event.additionalData.additionaldetail[0] };
    delete detail.hoursPlannedArtefact;
    delete detail.hoursActualArtefact;
    const body = {
      ...event,
      start: startString,
      end: endString,
      additionalData: {
        ...additionalData,
        comment: data.comment,
        endtime: endString,
        employees: [
          {
            employee: {
              id: data.employeeId,
            },
            department: event.additionalData.employees[0].department,
          },
        ],
        additionaldetail: [
          {
            ...detail,
          },
        ],
        begintime: startString,
        state: data.state,
      },
    } as RosterContent;
    try {
      const result = await this.#eventRepository.updateEvent(body);
      this.messageBarService.success(`Tätigkeit wurde erfolgreich bearbeitet.`);
      const updated = result[0];
      const updatedId = updated?.id;
      if (!updatedId) return;
      this.#updateEventsMap((map) =>
        map.set(updatedId, { ...updated, resize: true })
      );
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
      this.refreshEvents();
    }
  }

  async createOrEditTask(
    resource?: (number | string)[],
    event?: RosterContent
  ): Promise<any | undefined> {
    const response = await this.#modal({
      component: GarageRosterTaskFormComponent,
      title: event ? 'Tätigkeit bearbeiten' : 'Tätigkeit erstellen',
      separateContentSection: true,
      surfaceControls: { minimize: true, dock: true },
      data: {
        resource: resource,
        event: event,
        resources: (this.resources() || []).flatMap((o) => o.children ?? []),
      },
    });
    if (response === 'DISMISSED' || response === 'CANCEL') return;
    else return response;
  }

  private async duplicateEvent(
    event: any,
    date?: Date,
    targetResource?: number | string
  ): Promise<void> {
    try {
      //make date timezone less
      const newDate = date
        ? new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        : undefined;

      const newStartDate =
        newDate?.toISOString().split('T')[0] + 'T' + event.start.split('T')[1];
      const newEndDate =
        newDate?.toISOString().split('T')[0] + 'T' + event.end.split('T')[1];
      // Backend-only response fields must not be sent back
      const additionalData = { ...event.additionalData };
      delete additionalData.billable;
      delete additionalData.creatorUser;
      delete additionalData.createdAt;
      const detail = { ...event.additionalData.additionaldetail[0] };
      delete detail.hoursPlannedArtefact;
      delete detail.hoursActualArtefact;
      const newEvent = [
        {
          ...event,
          id: null,
          date: newDate?.toISOString() ?? event.date,
          start: newDate ? newStartDate : event.start,
          end: newDate ? newEndDate : event.end,
          resource: targetResource ? [targetResource] : event.resource,
          additionalData: {
            ...additionalData,
            id: null,
            begintime: newDate ? newStartDate : event.additionalData.begintime,
            endtime: newDate ? newEndDate : event.additionalData.endtime,
            state: 'UNDEFINED',
            department: null,
            employees: event.additionalData.employees.map((emp: any) => ({
              ...emp,
              employee: {
                ...emp.employee,
                id: targetResource ? Number(targetResource) : emp.employee.id,
              },
              department: null,
            })),
            additionaldetail: [
              {
                ...detail,
                id: null,
                key: null,
                name: null,
                state: 'UNDEFINED',
              },
            ],
          },
        },
      ] as unknown as RosterContent;
      const result = await this.#eventRepository.createEvent(newEvent as any);
      if (!result.id) return;

      this.#events.update((o) => {
        if (!result.id) return o;
        const map = new Map(o);
        map.set(result.id, result);
        return map;
      });

      this.messageBarService.success(
        event.original?.['additionalData']
          ? 'Planeintrag'
          : 'Termin' + ` wurde dupliziert.`
      );
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    }
  }

  private closeContextMenu(): void {
    this.closeContextMenuOverlay?.();
    this.selectedEvents.set([]);
    this.#selectedEventIds.set(new Set());
    this.contextMenuEventDate.set(null);
  }

  #getNextMonday(): Date {
    const base = new Date(this.startOfPeriod());
    const day = base.getDay();
    const daysUntilNextMonday = day === 0 ? 1 : 8 - day;
    const monday = new Date(base);
    monday.setDate(base.getDate() + daysUntilNextMonday);
    monday.setHours(0, 0, 0, 0);
    return monday;
  }
}
